import { Download, Copy, ExternalLink } from "lucide-react";
import ad1 from "@/assets/fb-ad-1-plateau.jpg";
import ad2 from "@/assets/fb-ad-2-wasted-spend.jpg";
import ad3 from "@/assets/fb-ad-3-burned-vendors.jpg";
import ad4 from "@/assets/fb-ad-4-tracking.jpg";

const ads = [
  {
    id: 1,
    angle: "Plateau / New-Patient Pain",
    recommended: false,
    image: ad1,
    primary: `Dentists doing $1M+:\nIf you're stuck at 20–30 new patients a month and "trying more marketing" isn't moving collections… the problem isn't effort, it's the system.\n\nWe've analyzed hundreds of practices and see the same pattern: money going into postcards, Google, and Facebook… with no clear link to arrivals and production.\n\nWe built a No-Touch Practice Growth Analysis that shows you:\n• Your Practice Growth Score vs other $1M–$3M offices\n• Where your new-patient flow is leaking (marketing, phones, scheduling, case acceptance)\n• A 90-day roadmap to add predictable NPs with or without us\n\nNo call required. Answer a few questions, and we'll send your score and roadmap.`,
    headline: "Get Your Practice Growth Score (For $1M+ Practices)",
    description: "See where your marketing is leaking and how to fix it in 90 days.",
    button: "Learn More",
  },
  {
    id: 2,
    angle: "Wasted Marketing Spend / Leak Angle",
    recommended: true,
    image: ad2,
    primary: `Most $1M+ dental practices we analyze are wasting 30–50% of their marketing.\nNot because the ads are "bad"… but because no one tracks from lead → schedule → arrival → collections.\n\nOur No-Touch Practice Growth Analysis audits:\n• Your current spend (postcards + digital)\n• Your lead, schedule, and arrival numbers\n• Your collections reality\n\nThen we give you a Growth Score and a custom 90-day plan to plug the leaks.\n\nIf you're spending $3K+/month on growth and can't see exactly what it returns, this is for you.`,
    headline: "Stop Wasting 30–50% of Your Marketing Budget",
    description: "Get a custom score & roadmap based on your numbers.",
    button: "Learn More",
  },
  {
    id: 3,
    angle: "Burned-By-Vendors Angle",
    recommended: true,
    image: ad3,
    primary: `"We've tried marketing companies before. It didn't move the needle."\n\nIf that's you, this is not another sales call.\n\nOur No-Touch Practice Growth Analysis is what we do before we agree to take on a $1M+ office:\n• We review your size, NPs, and current marketing\n• We score your practice against our best performers\n• We send you a written breakdown: where you're strong, where you're leaking, and what we'd do first\n\nYou get the analysis and 90-day roadmap either way. If the math makes sense, we can talk. If not, you still know exactly what to fix.`,
    headline: "Before You Hire Another Agency, Get This",
    description: "Independent Growth Analysis for $1M+ dental practices.",
    button: "Learn More",
  },
  {
    id: 4,
    angle: "Tracking / Black-Box Angle",
    recommended: false,
    image: ad4,
    primary: `Are you spending thousands per month on marketing but can't answer:\n"Exactly how many new patients and dollars did this create last month?"\n\nThat's a measurement problem, not a marketing problem.\n\nOur No-Touch Practice Growth Analysis is built for $1M+ owners who want:\n• A clear view of lead → schedule → arrival → production\n• A simple Growth Score that shows how you stack up\n• A 90-day action plan to get predictable NPs\n\nWe've run dental marketing for 20+ years. Start with the numbers, then decide if you even need a new vendor.`,
    headline: "Turn Your Marketing From Guesswork Into a Dashboard",
    description: "Get a no-touch Growth Analysis and 90-day roadmap.",
    button: "Learn More",
  },
];

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

const FbAdsPage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-hero-bg py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-accent font-semibold text-sm tracking-wide uppercase mb-2">
            Facebook Ad Creatives
          </p>
          <h1 className="text-3xl md:text-4xl font-bold text-hero-foreground tracking-tight mb-3">
            Practice Growth Analysis — Ad Suite
          </h1>
          <p className="text-hero-foreground/60 max-w-2xl leading-relaxed">
            4 angles targeting $1M+ dental practice owners. Each ad drives to the{" "}
            <a href="/apply" className="text-accent hover:underline">
              Growth Analysis landing page
            </a>
            . Start with #2 or #3, use Dr. Patel video as creative, then iterate off the winner.
          </p>
        </div>
      </div>

      {/* Setup Notes */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-secondary rounded-xl border border-border p-6 mb-10">
          <h2 className="font-bold text-foreground mb-3">FB Ads Manager Setup</h2>
          <div className="grid sm:grid-cols-2 gap-4 text-sm text-muted-foreground">
            <div>
              <p className="font-medium text-foreground mb-1">Campaign Objective</p>
              <p>Conversions → Website Conversions (optimize for form submit)</p>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">Targeting</p>
              <p>Dentists, Dental Practice Owners, $1M+ revenue interests, 35-65 age</p>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">Destination URL</p>
              <p className="font-mono text-xs break-all">
                {window.location.origin}/apply?utm_source=facebook&utm_medium=paid&utm_campaign=growth-analysis
              </p>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">Placements</p>
              <p>Facebook Feed, Instagram Feed, Facebook Stories (start manual, then expand)</p>
            </div>
          </div>
        </div>

        {/* Ad Cards */}
        <div className="space-y-12">
          {ads.map((ad) => (
            <div
              key={ad.id}
              className="bg-card rounded-xl border border-border overflow-hidden shadow-sm"
            >
              {/* Ad Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-hero-bg text-hero-foreground flex items-center justify-center text-sm font-bold">
                    {ad.id}
                  </span>
                  <div>
                    <h3 className="font-bold text-foreground">{ad.angle}</h3>
                  </div>
                </div>
                {ad.recommended && (
                  <span className="bg-accent/10 text-accent text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                    Recommended
                  </span>
                )}
              </div>

              <div className="grid md:grid-cols-2">
                {/* Creative Preview */}
                <div className="p-6 flex items-center justify-center bg-secondary/50">
                  <div className="w-full max-w-sm">
                    {/* FB Post Mockup */}
                    <div className="bg-card rounded-lg border border-border overflow-hidden shadow-sm">
                      <div className="p-3 flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-accent/20" />
                        <div>
                          <p className="text-xs font-semibold text-foreground">XPress Promotion</p>
                          <p className="text-[10px] text-muted-foreground">Sponsored · 🌐</p>
                        </div>
                      </div>
                      <p className="px-3 pb-2 text-xs text-foreground/80 line-clamp-3">
                        {ad.primary.split("\n")[0]}
                      </p>
                      <img
                        src={ad.image}
                        alt={ad.angle}
                        className="w-full aspect-square object-cover"
                        loading="lazy"
                        width={1080}
                        height={1080}
                      />
                      <div className="p-3 border-t border-border">
                        <p className="text-[10px] text-muted-foreground uppercase">
                          {window.location.host}
                        </p>
                        <p className="text-xs font-semibold text-foreground truncate">
                          {ad.headline}
                        </p>
                        <p className="text-[10px] text-muted-foreground truncate">
                          {ad.description}
                        </p>
                      </div>
                      <div className="px-3 pb-3">
                        <div className="bg-secondary text-foreground text-xs font-semibold text-center py-2 rounded">
                          {ad.button}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Copy */}
                <div className="p-6 space-y-5">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                        Primary Text
                      </label>
                      <button
                        onClick={() => copyToClipboard(ad.primary)}
                        className="text-xs text-accent hover:underline flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                    <div className="bg-secondary rounded-lg p-4 text-sm text-foreground whitespace-pre-line leading-relaxed max-h-60 overflow-y-auto">
                      {ad.primary}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground block mb-1">
                      Headline
                    </label>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-foreground flex-1">{ad.headline}</p>
                      <button
                        onClick={() => copyToClipboard(ad.headline)}
                        className="text-muted-foreground hover:text-accent"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground block mb-1">
                      Description
                    </label>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-muted-foreground flex-1">{ad.description}</p>
                      <button
                        onClick={() => copyToClipboard(ad.description)}
                        className="text-muted-foreground hover:text-accent"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground block mb-1">
                      CTA Button
                    </label>
                    <p className="text-sm font-medium text-foreground">{ad.button}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Notes */}
        <div className="mt-12 mb-16 bg-secondary rounded-xl border border-border p-6">
          <h3 className="font-bold text-foreground mb-3">Launch Playbook</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
            <li>
              <span className="text-foreground font-medium">Start with Ad #2 or #3</span> — these
              have the strongest hook for jaded buyers
            </li>
            <li>
              <span className="text-foreground font-medium">Use Dr. Patel video as creative</span>{" "}
              — swap the static image for the testimonial video for higher engagement
            </li>
            <li>
              <span className="text-foreground font-medium">Run for 5–7 days</span> at $50–$100/day
              before judging performance
            </li>
            <li>
              <span className="text-foreground font-medium">Iterate off the winner</span> — double
              down on the angle that gets the most form completions, not clicks
            </li>
            <li>
              <span className="text-foreground font-medium">Add UTM parameters</span> to track
              which ad drives the highest-quality leads in GHL
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default FbAdsPage;
